import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, MessageCircle, ArrowRight, Youtube } from "lucide-react";
import { CONTACT } from "../data/content";
import useSiteContent from "../hooks/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useContact = () => {
  const cms = useSiteContent();
  return { ...CONTACT, ...(cms.contact || {}) };
};

export const Contact = () => {
  const CONTACT = useContact();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada. Entraremos em contacto brevemente.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar a mensagem.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/20 px-4 py-3.5 text-sm font-light text-white focus:outline-none focus:border-white transition-colors duration-300 placeholder:text-white/30";

  return (
    <section id="contactos" data-testid="contact-section" className="bg-[#0A0A0A] text-white pt-36 md:pt-44 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="label-editorial text-clay-soft mb-6">Contactos</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-none">
            Fale com a <span className="italic">INN.PACT</span>
          </h2>

          <div className="mt-12 space-y-6 font-light text-white/70">
            <p data-testid="contact-address" className="flex items-start gap-4">
              <MapPin size={18} className="mt-1 shrink-0 text-white/40" />
              {CONTACT.address}
              <br />
              {CONTACT.city}
            </p>
            <p data-testid="contact-phones" className="flex items-start gap-4">
              <Phone size={18} className="mt-1 shrink-0 text-white/40" />
              <span>
                <a href={`tel:+351${(CONTACT.mobile || "").replace(/\s+/g, "")}`} className="hover:text-white transition-colors">Telemóvel: {CONTACT.mobile}</a>
                <br />
                <a href={`tel:+351${(CONTACT.phone || "").replace(/\s+/g, "")}`} className="hover:text-white transition-colors">Telefone: {CONTACT.phone}</a>
              </span>
            </p>
            <p data-testid="contact-email" className="flex items-start gap-4">
              <Mail size={18} className="mt-1 shrink-0 text-white/40" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors">
                {CONTACT.email}
              </a>
            </p>
            <p data-testid="contact-hours" className="flex items-start gap-4">
              <Clock size={18} className="mt-1 shrink-0 text-white/40" />
              {CONTACT.hours}
            </p>
            <a
              data-testid="contact-facebook"
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 hover:text-white transition-colors"
            >
              <Facebook size={18} className="shrink-0 text-white/40" />
              Facebook
            </a>
            <a
              data-testid="contact-instagram"
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 hover:text-white transition-colors"
            >
              <Instagram size={18} className="shrink-0 text-white/40" />
              Instagram
            </a>
            <a
              data-testid="contact-whatsapp"
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 hover:text-white transition-colors"
            >
              <MessageCircle size={18} className="shrink-0 text-white/40" />
              WhatsApp
            </a>
            {CONTACT.youtube && (
              <a
                data-testid="contact-youtube"
                href={CONTACT.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 hover:text-white transition-colors"
              >
                <Youtube size={18} className="shrink-0 text-white/40" />
                YouTube
              </a>
            )}
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="lg:col-span-6 lg:col-start-7 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome"
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              className={inputCls}
            />
          </div>
          <input
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Telemóvel (opcional)"
            className={inputCls}
          />
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="A sua mensagem"
            className={inputCls}
          />
          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={submitting}
            className="label-editorial bg-white text-black px-10 py-4 hover:bg-white/80 transition-colors duration-300 disabled:opacity-50"
          >
            {submitting ? "A enviar…" : "Enviar Mensagem"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export const Footer = () => {
  const CONTACT = useContact();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email: email.trim() });
      toast.success(res.data.message || "Subscrição efetuada!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível subscrever.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] text-white border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
        {/* Esquerda: logótipo + copyright */}
        <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
          <img
            src="/images/logo.png"
            alt="INN.PACT — Clínica de Imagem, Leiria"
            className="h-10 w-auto object-contain"
            style={{ filter: "invert(1)" }}
            loading="lazy"
            decoding="async"
          />
          <p className="text-[11px] text-white/40 font-light tracking-widest uppercase text-center md:text-left">
            © {new Date().getFullYear()} INN.PACT · Leiria
          </p>
        </div>

        {/* Direita: newsletter discreta + redes sociais */}
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="flex items-center gap-2 border-b border-white/25 focus-within:border-clay-soft transition-colors"
          >
            <input
              data-testid="newsletter-email-input"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Newsletter · o seu email"
              className="bg-transparent py-2 pr-2 text-sm font-light text-white focus:outline-none placeholder:text-white/35 w-56 max-w-full"
            />
            <button
              data-testid="newsletter-submit-btn"
              type="submit"
              disabled={submitting}
              aria-label="Subscrever newsletter"
              className="text-clay-soft hover:text-white transition-colors disabled:opacity-40 shrink-0"
            >
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="flex items-center gap-4 text-white/50">
            <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-white transition-colors">
              <Facebook size={18} />
            </a>
            <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition-colors">
              <Instagram size={18} />
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hover:text-white transition-colors">
              <MessageCircle size={18} />
            </a>
            {CONTACT.youtube && (
              <a href={CONTACT.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-white transition-colors">
                <Youtube size={18} />
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
